/********************************** WHITEBOPHIR ***************************************************
 * This class was taken and adapted for the EDUAC project from the WHITEBOPHIR project            *
 * https://github.com/lovasoa/whitebophir, keeping the same distribution and license conditions.  *
**************************************************************************************************
*/
import Vue from 'vue';
import Component from 'vue-class-component';
import {Emit, Mixins, Prop, Watch} from 'vue-property-decorator';
import {io} from 'socket.io-client';
import { ConflictResolutionMethod } from '@webcat/wbo-client';
import type { WboClient, WboDocument, WboAnyAnnotationData } from '@webcat/wbo-client';

import KEYS from '@/values/keys';
import {
    BOARD_EVENTS,
    DEFAULT_COLOR,
    HOMEWORK_TEMPLATE_DEFAULT_NAME,
    HOMEWORK_TEMPLATE_NAMES
} from '@/values/ed-whiteboard.values';
import {
    _HOMEWORKS_STORE,
    HOMEWORK_SOCKET_EVENTS,
    HOMEWORK_SOCKET_MESSAGES_TYPES, HOMEWORK_STATUS, HOMEWORKS_API, HOMEWORKS_EVENTS
} from '@/homework/values/homeworks.values';
import {HomeworksService} from '@/homework/services/homeworks.service';
import {
    _APP_STORE,
    CLOSE_BOARD_SOCKET_DISCONNECTING_EVENT,
    DISCONNECTION_ALERT_TYPES, NETWORK_SETTINGS,
    SHOW_DISCONNECTED_MODAL_EVENT,
    SHOW_SOCKET_DISCONNECTING_EVENT
} from '@/core/values/app.values';
import {Section} from '@/courses/models/section.model';
import EdPresentationModeFactory from '@/content/factories/ed-presentation-mode.factory';
import BrowserService from '@/providers/browser-utils/browser.service';
import EdPdfViewer from '@/components/common/ed-pdf-viewer/ed-pdf-viewer.vue';
import HomeworkBoardPageInfoResponse from '@/homework/models/homework-board-page-info-response.model';
import {OFFLINE_MODE_EVENT} from '@/core/values/core.values';
import {AuthProvider} from '@/auth/providers/auth.provider';
import EdBoardV2 from '@/components/common/ed-board/ed-board-v2.component.vue';
import TaskWhiteboardMixin from '@/mixins/common/task-whiteboard.mixin';
import EdWhiteboardMenu
    from '@/core/components/ed-meeting-transparency/ed-whiteboard-menu/ed-whiteboard-menu.component.vue';
import {BoardType} from '@/components/common/ed-board/ed-board.enum';
import { _MEETINGS_STORE } from '@/meetings/values/meetings.values';
import keys from '@/values/keys';
import EdWhiteboardMenuHistory from '@/core/components/ed-meeting-transparency/ed-whiteboard-menu-history/ed-whiteboard-menu-history.component.vue';
import {CHANGE_PRESENTATION_TASK_USER_EVENT} from '@/content/values/ed-presentation-mode.values';
import BoardPage from '@/homework/models/board-page.model';
import { WboVersionedAnnotationData } from '@webcat/wbo-client';

@Component({
    components: {
        'EdBoardV2': EdBoardV2,
        'EdPdfViewer': EdPdfViewer,
        EdWhiteboardMenu,
        EdWhiteboardMenuHistory,
    }
})
 class EdTaskBoardGroup extends Mixins(TaskWhiteboardMixin) {
    @Prop({required: true}) readonly board: string;
    @Prop({required: true}) readonly numberPages: number;
    @Prop({required: false}) readonly homeworkId: number;
    @Prop({required: false}) readonly section: Section;
    @Prop({required: false, default: false}) readonly canSendFeedback: boolean;
    @Prop({required: false}) readonly classId: number;
    @Prop({required: false}) readonly sectionId: number;
    @Prop({required: false}) readonly history: any;

    private isAddDisabled: boolean = true;
    private isAddingPage: boolean = false;
    private actualPageSizes: {id: number, width: number, height: number, top?: number }[] = [];
    private defaultColorAlreadySet = false;
    public historyPages = null;
    private pageSizes: {id: number, width: number, height: number, top?: number }[] = [];
    private ongoingConflictResolution: boolean =  false;
    private isThereAnyConflict: boolean = false;
    private managedWboClients: { [boardName: string]: WboClient; } = {};


    get userLoginId(): number {
        return this.$store.state.AuthStore.user.id;
    }

    get isPresentationStudentMode(): boolean {
        return this.$store.state.CourseStore.isPresentationStudentMode;
    }

    get isShowMenu(): boolean {
        return !this.isPresentationMode || ((this.isPresentationStudentMode && this.currentBoardUserId === this.userLoginId && !this.isExpirationHomework) ||
            ((this.$store.state.CourseStore.isPresentationTeacherMode || this.isPresentationModeGuestTeacher) && !this.isExpirationHomework));
    }

    get isShowHistory(){
        return !this.isHistory && this.isShowMenu;
    }

    get showSave(): boolean {
        return this.isBuyerRole;
    }

    get showFeedback(): boolean {
        return this.canSendFeedback && this.$store.state.AuthStore.role === KEYS.ROLES.PUBLISHER;
    }

    get isExpirationHomework(): boolean {
        return this.$store.state.HomeworksStore.presentationSectionSelected.expiration;
    }

    get isSaving(): boolean {
        return this.$store.state.HomeworksStore.isSavingHomework;
    }

    get clientId(): boolean {
        return this.$store.state.HomeworksStore.clientId;
    }

    get isHistory(): boolean {
        return this.history != null && this.history.show !== undefined;
    }

    get pagesHistory(): any[] {
        return this.boardPages.map(boardPage => {
            return {
                ...this.history,
                annotations: this.historyPages ? {
                    boardName: `${this.boardGroupHash}_${boardPage.page}`,
                    metadata: this.historyPages.annotationsPageMetadata.find(m => m.page === boardPage.page),
                    _children: Object.values(this.historyPages.annotationsPage[boardPage.page])
                }: {}
            };
        })
    }

    /**
     * Size scale factor to be used when calculating the actual size the page will occupy. It is 
     * variable depending on the original page's width and also on the container's width.
     */
    private getSizeScaleFactor(pageWidth: number): number {
        const containerWidth = this.pageContainer.offsetWidth;
        return containerWidth / pageWidth;
    }

    private callExternalTool(toolName: string, isSecondary: boolean, boardPage: number = 0): void {
        this.$edEventBus.$emit(`${BOARD_EVENTS.CHANGE_TOOGLE_TOOL}-${this.boardGroupHash}`, toolName, isSecondary, boardPage);
    }

    private showLoadingOverlay(): void {
        this.showLoading();
        this.isAddDisabled = true;
        this.boardMenu?.disableUndoOrRedoOptions();
    }

    public onBoardLoaded(pageNumber: number) {
        this.loadedPagesCount++;
        this.adjustPageLoaded(pageNumber);

        if (pageNumber >= 1) {
            // Just show as soon as the first page is ready
            if (!this.defaultColorAlreadySet) {
                this.boardMenu?.resetIcons();
                this.setDefaultColor();
                this.defaultColorAlreadySet = true;
            }
            this.finishLoading();
            this.$store.commit(_MEETINGS_STORE.MUTATIONS.UPDATE_INITIAL_BOARDS_LOADED_EMITED, true);
        }
        if (this.isAddingPage) {
            this.updateAddPage(pageNumber);
            return;
        }

    }

    public finishLoading(): void {
        this.hideLoading();
        this.isAddDisabled = false;
        this.boardMenu?.validateUndoOrRedoOption();
    }

    private updateAddPage(boardId: number): void {
        // this.updateBoardSizes(boardId);
        this.defineClientHeightHalf();
        this.isAddingPage = false;
        this.finishLoading();

    }

    public setDefaultColor(): void {
        if (this.showFeedback) {
            this.boardMenu?.setColor(DEFAULT_COLOR.FEEDBACK);
        } else if (this.isBuyerRole) {
            this.boardMenu?.setColor(DEFAULT_COLOR.DRAFT);
        } else {
            this.boardMenu?.setColor(DEFAULT_COLOR.DRAFT_TEACHER);
        }
    }

    private async validateBeforeSave(): Promise<boolean> {
        if (this.offlineMode) {
            return false;
        }
        if (this.isThereAnyBoardInSyncingProcess()) {
            this.$EdModal.createMessageModal({
                title: this.$t('homeworks.errorTitle'),
                body: this.$t('homeworks.boardsAreSyncing'),
                type: KEYS.EVENT_TYPES.DANGER
            });
            return false;
        }

        if (await this.isThereAnyBoardWithPendingAnnotations()) {
            this.$EdModal.createMessageModal({
                title: this.$t('homeworks.errorTitle'),
                body: this.$t('homeworks.boardsHavePendingAnnotations'),
                type: KEYS.EVENT_TYPES.DANGER
            });

            return false;
        }
        return true;
    }

    public async save(): Promise<void> {
        if (await this.validateBeforeSave() == false) {
            return;
        }
        this.homeworkSentByMe = true;
        this.saveWhiteboardHomework();
    }

    @Emit()
    public saveWhiteboardHomework() {
        // Just emit the event and nothing more.
    }

    public async sendFeedback(): Promise<void> {
        if (await this.validateBeforeSave() == false) {
            return;
        }
        this.homeworkSentByMe = true;
        this.sendWhiteboardHomeworkFeedback(this.boardGroupHash);
    }

    @Emit()
    public sendWhiteboardHomeworkFeedback(boardGroupHash: string): string {
        return boardGroupHash;
    }

    public async obtainHomeworkPages(): Promise<void> {
        if (!this.boardGroupName) {
            this.showCantOpenBoardError();
            return;
        }
        try {
            const response = this.isHistory ? 
                await HomeworksService.getHomeworkHistoryBoardPages(this.history.info.id) : 
                await HomeworksService.getHomeworkBoardPages(this.boardGroupName);
                
            this.boardPages = response;
            this.totalPages = response.length;
            this.boardPages.forEach(page => {
                page.boardType = BoardType.Task;
                // if (this.docKey) {
                //     page.docKey = this.docKey;
                // }
                if (page.addedPage) {
                    this.addedBoardPages.push(page);
                }
            });
            this.$store.commit(_HOMEWORKS_STORE.MUTATIONS.UPDATE_NUMBER_PAGES, this.totalPages);
            this.$nextTick(() => {
                this.$edEventBus.$emit(`${BOARD_EVENTS.ADD_EXTRA_PAGES}_${this.documentHash}`, this.addedBoardPages);
            });
        } catch (e) {
            this.showCantOpenBoardError();
        }
    }


    public createPage(optionSelected: any): void {
        if (this.offlineMode) {
            return;
        }
        this.isAddingPage = true;
        let templateNameType = optionSelected.type && optionSelected.type != HOMEWORK_TEMPLATE_DEFAULT_NAME
            ? HOMEWORK_TEMPLATE_NAMES.RESPONSE_PAPER
            : HOMEWORK_TEMPLATE_NAMES.BLANK;

        HomeworksService.addWhiteBoardPage(this.boardGroupHash, templateNameType)
            .then((response: HomeworkBoardPageInfoResponse) => {
                this.totalPages = response.totalPages;
                const page = {
                    width             : response.boardPage.width,
                    height            : response.boardPage.height,
                    page              : response.boardPage.page,
                    addedPage         : true,
                    addingPage        : true,
                    backgroundImageUrl: response.boardPage.backgroundImageUrl,
                    boardType         : BoardType.Task,
                    sectionType       : this.section.type
                };
                this.boardPages.push(page);
                this.addToBoardSizes(page);
                this.$edEventBus.$emit(`${BOARD_EVENTS.ADD_EXTRA_PAGES}_${this.documentHash}`, [page]);
                this.$store.commit(_HOMEWORKS_STORE.MUTATIONS.UPDATE_NUMBER_PAGES, this.totalPages);
                this.showLoadingOverlay();
            })
            .catch(() => {
                this.showMessage(
                    'courses.info.infoTitle',
                    'courses.errors.errorAddWhiteboardPage',
                    KEYS.EVENT_TYPES.DANGER,
                    null
                );
                this.isAddingPage = false;
            });
    }

    private updateBoard(page: any) {
        if (this.isAddingPage) {
            return;
        }

        let existsPage = this.boardPages.find(boardPage => boardPage.page === page.page);
        if (existsPage) {
            return;
        }

        this.isAddingPage = true;
        HomeworksService.obtainBoardUpdates(this.boardGroupHash, page.page)
            .then((response: HomeworkBoardPageInfoResponse) => {
                if (response && response.totalPages && response.boardPage) {
                    if (response.totalPages > this.totalPages) {
                        this.totalPages = response.totalPages;
                        this.$store.commit(_HOMEWORKS_STORE.MUTATIONS.UPDATE_NUMBER_PAGES, this.totalPages);
                    }
                    const newPage = {
                        width             : response.boardPage.width,
                        height            : response.boardPage.height,
                        page              : response.boardPage.page,
                        addedPage         : true,
                        addingPage        : true,
                        backgroundImageUrl: response.boardPage.backgroundImageUrl,
                        boardType         : BoardType.Task
                    };
                    this.boardPages.splice(response.boardPage.page - 1, 0,
                        newPage);
                    this.addToBoardSizes(newPage);
                    this.$edEventBus.$emit(`${BOARD_EVENTS.ADD_EXTRA_PAGES}_${this.documentHash}`, [newPage]);
                    this.showLoadingOverlay();
                }
            })
            .catch(() => this.isAddingPage = false);
    }


    @Watch('currentBoardPage')
    private onCurrentBoardPageChanged(newVal: number, oldVal: number) {
        if (oldVal != newVal) {
            this.emitPageChanged();
        }
    }

    public checkConnectionStatus(isDisconnected: boolean): void {
        if (isDisconnected === undefined || this.homeworkSent) {
            return;
        }

        this.isPreviousDisconnected = isDisconnected;
        if (isDisconnected) {
            this.showDisconnectedAlert();
            return;
        } else if (!isDisconnected && this.isBoardPreviousDisconnectionSocket) {
            let messageType: string = '';
            if (!this.isPresentationTeacherMode && !this.isPresentationModeGuestTeacher) {
                messageType = DISCONNECTION_ALERT_TYPES.toast;
            }
            this.$edEventBus.$emit(CLOSE_BOARD_SOCKET_DISCONNECTING_EVENT, messageType);
        }

        if (!this.showSave && !this.showFeedback) {
            this.showDisable = false;
        }
    }

    public showDisconnectedAlert() {
        if (this.isPresentationMode) {
            this.$edEventBus.$emit(SHOW_SOCKET_DISCONNECTING_EVENT, {isBoard: true});
        } else {
            if (!this.showSave && !this.showFeedback) {
                this.showDisable = true;
            }
            this.$edEventBus.$emit(SHOW_SOCKET_DISCONNECTING_EVENT, {
                isBoard    : true,
                messageType: DISCONNECTION_ALERT_TYPES.toast
            });
        }
    }

    public connect(): any {
        // Destroy socket if one already exists
        this.disconnectSocket();

        if (this.isHistory) {
            return;
        }

        try {
            this.socket = io(this.wboTaskSocketUrl, {
                ...NETWORK_SETTINGS.socketIo.connection,
                transports: ['websocket'],
                auth: {token: AuthProvider.getToken()},
            });

            const manager = this.socket.io;
            manager.on('reconnect_attempt', () => {
                this.socket.auth = {token: AuthProvider.getToken()};
            });

            //Receive connect client socket event from the server
            this.socket.on('connect', async () => {
                this.$store.commit(_HOMEWORKS_STORE.MUTATIONS.SET_CLIENT_ID, this.socket.id);
                this.checkConnectionStatus(false);
            });

            // Handle reconnection attempts
            manager.on('reconnect_attempt', (attemptNumber) => {
            });

            // Handle successful reconnection
            manager.on('reconnect', async (attemptNumber) => {
                if (!this.showSave && !this.showFeedback) {
                    this.loadedPagesCount = 0;
                    this.$edEventBus.$emit(BOARD_EVENTS.RECONNECT_BOARD);
                }
            if (await this.validateHomeworkStatusOnReconnection()) {
                    this.setupPagesAddedWhileOffline();
                } else {
                    this.isPreviousDisconnected = false;
                    this.showBoardNotAvailableMessage();
                }
            });

            // Handle reconnection error
            manager.on('reconnect_error', (error) => {
            // console.error('Reconnection error:', error);
            });

            // Reconnection was not possible, giving up
            manager.on('reconnect_failed', () => {
                this.showBoardNotAvailableMessage();
            });

            //Receive disconnected socket event from the server
            this.socket.on('disconnect', () => {
                this.$store.commit(_HOMEWORKS_STORE.MUTATIONS.SET_CLIENT_ID, '');
                this.$nextTick(() => {
                    if (this.socket) {
                        this.checkConnectionStatus(true);
                    }
                });
            });

            this.socket.on('board_read_only', () => {
                this.showBoardNotAvailableMessage();
            });

            this.socket.on('board_deleted', () => {
                this.showBoardNotAvailableMessage();
            });

            this.socket.on('board_joined', (msg: any) => {
                if (!msg.boardName) {
                    return;
                }
                this.$edEventBus.$emit(`${msg.boardName}${BOARD_EVENTS.JOINED}`, msg);
            });

            this.socket.on('board_annotation_updated', ({
                                                            operationId,
                                                            fullName
                                                        }: { operationId: number, fullName: string }) => {
                let bodyMessage = this.$t('homeworks.newHomeworkVersionUploaded');
                if (fullName) {
                    bodyMessage = bodyMessage.toString().replace('{fullName}', fullName);
                } else {
                    bodyMessage = bodyMessage.toString().replace('{fullName}', this.$t('homeworks.someone').toString());
                }
                this.showMessage('homeworks.homeworkUpdated', bodyMessage, KEYS.EVENT_TYPES.WARNING,
                    () => {
                        if (this.isPresentationMode) {
                            this.$edEventBus.$emit(CHANGE_PRESENTATION_TASK_USER_EVENT);
                        } else {
                            this.$edEventBus.$emit(BOARD_EVENTS.REFRESH_BOARD)
                        }
                    });
            });

            this.socket.on('board_updated', (page: number) => {
                this.updateBoard(page);
            });
        } catch (error) {
            this.showBoardNotAvailableMessage();
        }
    }


    private async validateHomeworkStatusOnReconnection(): Promise<boolean> {
        try {
            const response = await HomeworksService.getHomeworkStatus(this.homeworkId);
            return response.status === HOMEWORK_STATUS.DRAFT || response.status === HOMEWORK_STATUS.FEEDBACK_DRAFT;
        } catch (e) {
            return true; // If service can't be reached, just let the user continue working
        }
    }

    private async setupPagesAddedWhileOffline() {
        if (this.isHistory) {
            return;
        }
        try {
            const currentAddedPagesCount = this.addedBoardPages?.length || 0;
            const allPages: BoardPage[] = await HomeworksService.getHomeworkBoardPages(this.boardGroupName);
            if (!allPages || allPages.length == 0) {
                return;
            }
            const allAddedPages = allPages.filter(page => page.addedPage);
            const extraAddedPages: BoardPage[] = [];
            for (let i = currentAddedPagesCount; i < allAddedPages.length; i++) {
                extraAddedPages.push(allAddedPages[i]);
            }
            if (extraAddedPages.length === 0) {
                return;
            }
            this.$store.commit(_HOMEWORKS_STORE.MUTATIONS.UPDATE_NUMBER_PAGES, allPages.length);
            this.$nextTick(() => {
                this.$edEventBus.$emit(`${BOARD_EVENTS.ADD_EXTRA_PAGES}_${this.documentHash}`, extraAddedPages);
            });

            this.boardPages = allPages;
            this.totalPages = allPages.length;
            this.addedBoardPages.push(...extraAddedPages);
        } catch (e) {
            this.showCantOpenBoardError();
        }
    }

    private initConnect(): void {
        this.connect();
        this.obtainHomeworkPages();
    }

    private emitPageChanged(): void {
        if (this.section && this.isShowMenu) {
            const topicId = this.section.classId;
            const content = {
                sectionId : this.section.sectionId,
                homeworkId: this.homeworkId,
                page      : this.currentBoardPage,
                device    : BrowserService.getFullBrowserInfo()
            };
            this.$EdSocket.emit(HOMEWORK_SOCKET_EVENTS.changePage,
                EdPresentationModeFactory.createWebSocketMessageRequest(HOMEWORK_SOCKET_MESSAGES_TYPES.changePage,
                    String(topicId), false,
                    JSON.stringify(content)));
        }
    }


    public scrollToPage(targetPageNumber: number): void {
        if (targetPageNumber <= 0 || targetPageNumber > this.numberPages) {
            return;
        }
        const targetBoardSizeInfo = this.actualPageSizes[targetPageNumber - 1];
        const verticalOffsetToScroll = targetBoardSizeInfo.top;
        const isWithinBounds = verticalOffsetToScroll >= 0 && verticalOffsetToScroll < this.totalHeight;
        if (isWithinBounds && targetPageNumber != this.currentBoardPage) {
            this.pageContainer.scrollTo({
                top     : verticalOffsetToScroll,
                behavior: 'smooth'
            });
        }
    }

    private validateCurrentVersion(): void {
        if (!this.showSave && !this.showFeedback) {
            this.initConnect();
            return;
        }
        this.initConnect();
    }


    private showDifferentVersionMessage(localAction: Function, remoteAction: Function, mergeAction: Function,
                                        conflictingChange: WboAnyAnnotationData): void {
        let bodyMessage = this.$t('homeworks.homeworkConflictVersion');
        const conflict = conflictingChange as WboVersionedAnnotationData;
        const conflictingChangeDate = conflict.cdate ? new Date(conflict.cdate).toLocaleString() : '';
    
        bodyMessage = bodyMessage.toString()
            .replace('{date}', conflictingChangeDate);

        this.$EdModal.createConfirmationModal({
            body   : bodyMessage,
            actions: [
                {
                    label : this.$t('homeworks.localVersion'),
                    action: localAction
                },
                {
                    label : this.$t('homeworks.remoteVersion'),
                    mode  : 'outline',
                    action: remoteAction
                },
                {
                    label : this.$t('homeworks.mergeVersions'),
                    mode  : 'outline',
                    action: mergeAction
                }
            ]
        });
    }

    private checkOffline(offline: boolean): void {
        if (this.isHistory || (!this.showSave && !this.showFeedback)) {
            return;
        }

        if (offline) {
            this.isPreviousOffline = offline;
            return;
        }

        this.isPreviousOffline = offline;
    }


    public scroll(event: any): void {
        const scrollTop : number = event.target.scrollTop;
        this.pdfViewer?.scrollTo(0, scrollTop);
    }

    /**
     * This is a handler for the 'onChangeSelectedPage' event triggered by the <ed-pdf-viewer> component when a scroll
     *  event makes the selected page change.
     * @param page
     */
    public onChangeSelectedPageInViewer(page: number): void {
        this.defineCurrentPage(page);
    }

    /**
     * This gets called when the <ed-pdf-viewer> component is ready to start rendering the PDF pages. The passed
     * page sizes are those of that the PDF viewer calculated that will use to actually render those in its container.
     * It does not include the ones that were added.
     * @param pageSizes
     */
    public onInitRenderInViewer(pageSizes: {id: number, width: number, height: number }[]): void {
        this.actualPageSizes = pageSizes;
        // For each element, add a new property that indicates the starting point of the page in the scroll
        this.actualPageSizes.forEach((page, index) => {
            page.top = index === 0 ? 0 : this.actualPageSizes[index - 1].top + this.actualPageSizes[index - 1].height;
        });
        // Add the pages that were added manually
        this.addedBoardPages.forEach((page, index) => {
            this.actualPageSizes.push({
                id    : page.page,
                width : page.width * this.getSizeScaleFactor(page.width),
                height: page.height * this.getSizeScaleFactor(page.width),
                top   : this.actualPageSizes[this.actualPageSizes.length - 1].top + this.actualPageSizes[this.actualPageSizes.length - 1].height
            });
        });


        this.totalHeight = this.actualPageSizes.reduce((acc, page) => acc + page.height, 0);

        this.$nextTick(() => {
            this.$edEventBus.$emit(`${BOARD_EVENTS.ADD_EXTRA_PAGES}_${this.documentHash}`, this.addedBoardPages);
        });
    }



    private isThereAnyBoardInSyncingProcess(): boolean {
        return this.getBoardsInSyncingProcess().length > 0;
    }

    private getBoardsInSyncingProcess(): WboClient[] {
        const allClients = Object.values(this.managedWboClients);
        return allClients.filter(client => client.syncing);
    }

    private async isThereAnyBoardWithPendingAnnotations(): Promise<boolean> {
        const boardsWithPendingAnnotations = await this.getBoardsWithPendingAnnotations();
        return boardsWithPendingAnnotations.length > 0;
    }

    private async getBoardsWithPendingAnnotations(): Promise<WboClient[]> {
        const allClients = Object.values(this.managedWboClients);
        let boardsWithPendingChanges = [];
        for (const client of allClients) {
            if (await client.hasPendingChanges() === true) {
                boardsWithPendingChanges.push(client);
            }
        }
        return boardsWithPendingChanges;
    }

    private getBoardsWithConflict(): WboClient[] {
        const allClients = Object.values(this.managedWboClients);
        return allClients.filter(client => client.conflicted);
    }

    private syncAllBoards(): void {
        const allClients = Object.values(this.managedWboClients);
        allClients.forEach(client => client.syncBoard(true));
    }

    async resolveConflicts(conflictResolutionMethod: ConflictResolutionMethod): Promise<void> {
        try {
            const conflictedBoards = this.getBoardsWithConflict();
            this.showLoadingOverlay();
            await Promise.all(conflictedBoards.map(board => board.resolveConflict(conflictResolutionMethod)));
        } catch (e) {
            this.$EdModal.createMessageModal({
                title: this.$t('homeworks.errorTitle'),
                body: this.$t('homeworks.conflictResolutionFailed'),
                type: KEYS.EVENT_TYPES.DANGER
            });
            this.hideLoading();
        } finally {
            this.hideLoading();
        }

    }


    get pageContainerStyles(): string {
        return `height: ${this.totalHeight}px;`;
    }

    public onBoardReady(pageNumber: number) {
        if (this.isAddingPage) {
            this.updateAddPage(pageNumber);
        }
        this.finishLoading();
    }

    private addToBoardSizes(page: any) {
        const alreadyAddedPageSize = this.actualPageSizes.find(actualPageSize => actualPageSize.id === page.page);
        if (alreadyAddedPageSize) {
            return;
        }
        const actualPageSizeToAdd = {
            id    : page.page,
            width : page.width * this.getSizeScaleFactor(page.width),
            height: page.height * this.getSizeScaleFactor(page.width),
            top   : this.actualPageSizes[this.actualPageSizes.length - 1].top + this.actualPageSizes[this.actualPageSizes.length - 1].height
        };
        this.actualPageSizes.push(actualPageSizeToAdd);

        this.totalHeight += actualPageSizeToAdd.height;
    }


    private async loadHistory(): Promise<void> {
        if (!this.isHistory) {
            return;
        }
        try {
            const response: any = await HomeworksService.obtainHistoryBoardAnnotations(this.history.info.id);
            this.historyPages = response;
            this.$nextTick(() => this.$edEventBus.$emit(BOARD_EVENTS.HISTORY_READY));
        } catch (reason) {
            this.$EdModal.createMessageModal({
                title: this.$t('courses.info.infoTitle'),
                body: this.$t('courses.errors.errorLoadingAnnotations'),
                type: keys.EVENT_TYPES.DANGER
            });
        }
    }

    /**
     * Referenced by the .vue file
     */
    private pageHistory(page:number){
        const cloneHistory = { ...this.history };
        if (this.historyPages!=null){
            let annotation  = this.historyPages.annotationsPage[page];
            let metadata  = this.historyPages.annotationsPageMetadata.find(m=> m.page === page);
            let boardName = this.boardGroupHash + '_' + page;
            let children = [];
            for (let prop in annotation) {
                children.push(annotation[prop]);
            }
            cloneHistory['annotations'] = { boardName, _children: children, metadata: metadata };
        }
        return cloneHistory;
    }

    public showBoardNotAvailableMessage() {
        this.homeworkSent = true;
        const action = () => {
            if (this.isPresentationMode) {
                this.$edEventBus.$emit(CHANGE_PRESENTATION_TASK_USER_EVENT);
                return;
            }
            this.$edEventBus.$emit(HOMEWORKS_EVENTS.RELOAD_STUDENT_HOMEWORKS_TEACHER_WIDGET);
            this.$edEventBus.$emit(HOMEWORKS_EVENTS.WHITEBOARD_CLOSED_ON_DELETE, {courseId: this.courseId});
            this.$edEventBus.$emit(HOMEWORKS_EVENTS.RELOAD_SECTION_TEACHER_WIDGET);
        };

        if (this.homeworkSentByMe || this.isPresentationMode) {
            action();
        }
        else {
            this.$EdModal.closeConfirmationModal();
            this.showMessage('courses.info.infoTitle',  this.showFeedback ? 'courses.grade.feedbackAlreadySubmitted' : 'courses.info.homeworkNotAvailable',
                KEYS.EVENT_TYPES.WARNING, () => {
                    action();
                    this.$EdModal.closeModal();
                });
        }

    }

    /**
     * This method is called when a conflict is detected on any of its managed boards.
     */
    onAnnotationConflict(localDocument: WboDocument, conflictingChange: WboAnyAnnotationData) {
        this.isThereAnyConflict = true;
        if (this.isThereAnyConflict && !this.ongoingConflictResolution) {
            this.ongoingConflictResolution = true;

            this.showDifferentVersionMessage(() => {
                    this.resolveConflicts(ConflictResolutionMethod.KeepLocal);
                    this.ongoingConflictResolution = false;
                    this.isThereAnyConflict = false;
                },
                () => {
                    this.resolveConflicts(ConflictResolutionMethod.KeepRemote);
                    this.ongoingConflictResolution = false;
                    this.isThereAnyConflict = false;
                },
                () => {
                    this.resolveConflicts(ConflictResolutionMethod.Merge);
                    this.ongoingConflictResolution = false;
                    this.isThereAnyConflict = false;
                },
                conflictingChange);
        }

    }

    onWboClientCreated(client: WboClient) {
        this.managedWboClients[client.getBoardName] = client;
    }

    mounted() {
        this.showLoadingOverlay();
        this.validateCurrentVersion();
        // this.init();
        this.loadHistory();
        this.$edEventBus.$on(BOARD_EVENTS.CLOSE_LOADING_BOARD_TASK, this.onBoardLoaded);
        this.$edEventBus.$on(BOARD_EVENTS.INIT_BOARD_TASK, this.finishBoardInit);
        this.$edEventBus.$on(OFFLINE_MODE_EVENT, this.checkOffline);
        this.$edEventBus.$on(BOARD_EVENTS.BOARD_READY, this.onBoardReady);

        this.$store.commit(_HOMEWORKS_STORE.MUTATIONS.SET_BOARD_ACTIVE, true);
        window['managedWboClients'] = this.managedWboClients;
    }

    beforeDestroy() {
        this.disconnectSocket();
        this.$edEventBus.$off(BOARD_EVENTS.CLOSE_LOADING_BOARD_TASK, this.onBoardLoaded);
        this.$edEventBus.$off(BOARD_EVENTS.INIT_BOARD_TASK, this.finishBoardInit);
        this.$edEventBus.$off(OFFLINE_MODE_EVENT, this.checkOffline);
        this.$edEventBus.$off(BOARD_EVENTS.BOARD_READY, this.onBoardReady);
        this.$store.commit(_HOMEWORKS_STORE.MUTATIONS.SET_BOARD_ACTIVE, false);

        if (this.socket?.io) {
            const manager = this.socket.io;
            manager.off('reconnect_attempt');
            manager.off('reconnect');
            manager.off('reconnect_error');
            manager.off('reconnect_failed');
        }

        if ((this.isPreviousDisconnected || this.isPreviousOffline) && (this.showSave || this.showFeedback)
            && !this.isPresentationMode) {
            this.$EdToast.hide();
            this.$edEventBus.$emit(SHOW_DISCONNECTED_MODAL_EVENT, this.isPreviousDisconnected);
            this.$store.commit(_APP_STORE.MUTATIONS.UPDATE_PREVIOUS_DISCONNECTION_SOCKET, true);
        }
        this.managedWboClients = null;
    }

    activated() {
        // Trigger global event bus event that will cause that every EdBoard component recalculate its scaling
        this.$edEventBus.$emit(BOARD_EVENTS.RESIZE_BOARD);
    }

}

export default Vue.component('ed-task-board-group', EdTaskBoardGroup);
